import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormControl } from '../../tailwindcomponents';
import { User } from "./../../types/user";
import { useSelector } from 'react-redux'
import { RootState } from '../../reducer';
import { Input } from './../../tailwindcomponents/';
import { IconButton } from './../../tailwindcomponents';
import cn from '../../tailwindcomponents/cn';
import { getPopoverPositionStyle } from '../../tailwindcomponents/popoverPosition';
import { useAnchorRect } from '../../tailwindcomponents/useAnchorRect';
import { Close } from '../icons';
import ProfileImg from '../Images/ProfileImg';
import userApi from '../../api/userApi';

type EmployeeSearchBaseProps = {
  place_holder: string,
  reset?: boolean,
  /** Caps how many matches are rendered. The list scrolls; it is not paginated. */
  maxRows?: number
  inputWidth?: number,
  defaultEmployee?: string,
  clearOnSelect?: boolean,
  filterByIds?: number[],
  required?: boolean,
  searchByAbbreviation?: boolean; // To search for abbreviated names instead of regular names
  autoFocusOnInput?: boolean;
  customCloseFunction?: Function;
  disabled?: boolean;
  showDisabledEmployees?: boolean;
}

/**
 * Discriminated on `fullUser`:
 *  - `fullUser` set     -> the whole `User`, or `null` when the search is cleared.
 *  - `fullUser` omitted -> the employee id, or `0` when the search is cleared.
 * Handlers MUST handle the cleared value: the callback fires on the 'X' button,
 * on emptying the input, and whenever the `reset` prop flips to true.
 */
type EmployeeSearchProps =
  | (EmployeeSearchBaseProps & { fullUser: true, onSelectedEmployee: (employee: User | null) => void })
  | (EmployeeSearchBaseProps & { fullUser?: false, onSelectedEmployee: (employeeId: number) => void });

const EmployeeSearch: FunctionComponent<EmployeeSearchProps> = ({ customCloseFunction, place_holder, onSelectedEmployee, required = false, reset = false, maxRows = 50, inputWidth = 100, defaultEmployee = "", clearOnSelect = false, fullUser = false, filterByIds = [], searchByAbbreviation = false, autoFocusOnInput = false, disabled = false, showDisabledEmployees = false }) => {
  // Props are a discriminated union on `fullUser`, so this callback is a union of two
  // signatures and TS intersects its parameters to `never`. `fullUser` alone decides
  // which shape we emit, so widen once here instead of casting at all four emit sites.
  const notifySelection = onSelectedEmployee as (value: User | number | null) => void;
  const [employeeSearchString, setEmployeeSearchString] = useState(defaultEmployee);
  const [employees, setFilteredEmployees] = useState(Array<User>());
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const dropdownRef = useRef<HTMLUListElement | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const store = useSelector((state: RootState) => state);
  const [allEmployees, setAllEmployees] = useState<User[]>(store.employees.all_employees.filter(s => s.visible));

  useEffect(() => { setHighlightedIndex(0); }, [employees]);

  useEffect(() => {
    dropdownRef.current
      ?.querySelector<HTMLLIElement>('[data-ds-active="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex]);

  useEffect(() => {
    if (reset === true) {
      resetSearch();
    }
    if (filterByIds.length > 0) {
      let tempEmployees: Array<User> = [];
      tempEmployees = store.employees.all_employees.filter(s => s.visible).filter(s => filterByIds.includes(s.Id));
      
      setAllEmployees(tempEmployees);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset, filterByIds, store])

  //Store doesn't load the employees fast enough, this updates allEmployees when the store updates
  useEffect(() => {
    
      const tempEmployees = store.employees.all_employees.filter(s => s.visible);
    
      if(showDisabledEmployees === true)
      {
        userApi.getDisabledUsers().then(res => {
          setAllEmployees([...tempEmployees, ...res]);  
        });
      }
      else
      {
        if (allEmployees.length <= 0) {
          setAllEmployees(tempEmployees);
        }
      }
      
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.employees.all_employees]);

  
  const resetSearch = () => {
    customCloseFunction && customCloseFunction(); //IF customCloseFunction not undefined run customCloseFunction
    setFilteredEmployees([]);
    setEmployeeSearchString("");
    if (fullUser) {
      notifySelection(null);
    }
    else {
      notifySelection(0);
    }
  }
  const onSelectRow = (id: number) => {
    // NOTE: rows are keyed by `User.Id` but matched against `user_id`; these are distinct
    // fields that coincide for current data. Guard rather than change the emitted value --
    // non-fullUser consumers are split between looking the result up by `.Id` and `.user_id`.
    const selected = allEmployees.find(s => s.user_id === id);
    if (!selected) return;
    setEmployeeSearchString(clearOnSelect === true ? "" : selected.name);
    if (fullUser === true) {
      notifySelection(selected);
    }
    else {
      notifySelection(id);
    }
    setFilteredEmployees([]);
  }

  const onEmployeeSearchString = (value: string) => {
    if (allEmployees.length < 50) {
      setAllEmployees(store.employees.all_employees.filter(s => s.visible));
    }
    if (value.length === 0 || value.length === null) {
      resetSearch();
    }
    else {
      const match = searchByAbbreviation
        ? allEmployees.filter(s => s.employee_abbreviation != null && s.employee_abbreviation.toUpperCase().startsWith(value.toUpperCase()))
        : allEmployees.filter(s => s.name.toUpperCase().startsWith(value.toUpperCase()));
      // Cap at render time so the list state is always exactly what is on screen -
      // keyboard nav indexes into it and must not be able to highlight a hidden row.
      setFilteredEmployees(match.slice(0, maxRows));
      setEmployeeSearchString(value);
    }
  }

  useEffect(() => {
    if (employees.length === 0) return;
    const onDocDown = (e: MouseEvent) => {
      if (anchorRef.current?.contains(e.target as Node)) return;
      if (dropdownRef.current?.contains(e.target as Node)) return;
      setFilteredEmployees([]);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [employees.length]);

  const onInputKeyDown = (e: React.KeyboardEvent) => {
    if (employees.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(i => Math.min(i + 1, employees.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      const target = employees[highlightedIndex];
      if (target) { e.preventDefault(); onSelectRow(target.Id); }
    } else if (e.key === 'Escape') {
      setFilteredEmployees([]);
    }
  }

  const open = employees.length > 0;
  const rect = useAnchorRect(open, anchorRef);
  const popoverPosition = rect ? getPopoverPositionStyle(rect) : null;

  return (
    <FormControl fullWidth={true} className="relative" ref={anchorRef}>
      <Input
        multiline={false}
        label_text={place_holder}
        value={employeeSearchString}
        help_text=""
        required={required}
        onTextChange={(value: string) => onEmployeeSearchString(value)}
        onKeyDown={onInputKeyDown}
        type="text"
        width={inputWidth}
        icon={employeeSearchString.length > 0 ? <IconButton onClick={() => resetSearch()}><Close color='error' /></IconButton> : <div></div>}
        iconPlacement="end"
        autoFocus={autoFocusOnInput}
        disabled={disabled}
      />
      {open && rect && createPortal(
        <ul
          ref={dropdownRef}
          role="listbox"
          style={popoverPosition!.style}
          className={cn('ds-menu scrollbar-thin', popoverPosition!.className)}
        >
          {employees.map((row, index) => (
            <li
              key={row.Id}
              role="option"
              aria-selected={index === highlightedIndex}
              data-ds-active={index === highlightedIndex ? 'true' : undefined}
              onMouseEnter={() => setHighlightedIndex(index)}
              onClick={() => onSelectRow(row.Id)}
              className="ds-menu-item"
            >
              <ProfileImg imgKey={index} imgSize="xs" userName={row.userName?.split('@')[0]} />
              <span className="truncate">{row.name}</span>
            </li>
          ))}
        </ul>,
        document.getElementById('root') ?? document.body,
      )}
    </FormControl>
  );
};
export default EmployeeSearch;